import {
  findNext,
  findPrevious,
  getSearchQuery,
  SearchQuery,
  setSearchQuery,
} from "@codemirror/search";
import type { EditorView } from "@codemirror/view";
import { useEffect, useMemo, useRef, useState } from "react";
import { ToggleButton } from "../HeadingToggle";
import { Icon } from "../Icon";
import { Tooltip } from "../Tooltip";
import {
  SearchBarCount as Count,
  SearchBarIcon as SearchIcon,
  SearchBarInput as Input,
  SearchBarRow as Row,
} from "./searchBarStyles";

const MATCH_COUNT_CAP = 10000;

function countMatches(
  view: EditorView,
  query: SearchQuery
): { count: number; capped: boolean } {
  if (!query.valid) return { count: 0, capped: false };
  const cursor = query.getCursor(view.state.doc);
  let count = 0;
  for (let i = 0; i < MATCH_COUNT_CAP; i++) {
    const next = cursor.next();
    if (next.done) return { count, capped: false };
    count++;
  }
  return { count, capped: true };
}

type Props = {
  view: EditorView | null;
  query: string;
  onChange: (query: string) => void;
  onClose: () => void;
};

export function SearchBar({ view, query, onChange, onClose }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const initial = useMemo(
    () => (view ? getSearchQuery(view.state) : new SearchQuery({ search: "" })),
    [view]
  );
  const [caseSensitive, setCaseSensitive] = useState(initial.caseSensitive);
  const [wholeWord, setWholeWord] = useState(initial.wholeWord);
  const [regexp, setRegexp] = useState(initial.regexp);

  const searchQuery = useMemo(
    () => new SearchQuery({ search: query, caseSensitive, wholeWord, regexp }),
    [query, caseSensitive, wholeWord, regexp]
  );

  useEffect(() => {
    if (!view) return;
    view.dispatch({ effects: setSearchQuery.of(searchQuery) });
  }, [view, searchQuery]);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const { count: n, capped } = useMemo(
    () =>
      view && searchQuery.valid
        ? countMatches(view, searchQuery)
        : { count: 0, capped: false },
    [view, searchQuery]
  );

  const countText = !query
    ? ""
    : n === 0
      ? "No results"
      : `${n}${capped ? "+" : ""} result${n === 1 && !capped ? "" : "s"}`;

  return (
    <Row>
      <SearchIcon>
        <Icon name="search" size={12} />
      </SearchIcon>
      <Input
        ref={inputRef}
        type="text"
        placeholder="Find in code…"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (!view) return;
            if (e.shiftKey) findPrevious(view);
            else findNext(view);
          } else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
          }
        }}
        spellCheck={false}
      />
      {countText && <Count>{countText}</Count>}
      <Tooltip content="Match case">
        <ToggleButton
          aria-pressed={caseSensitive}
          onMouseDown={(e) => e.preventDefault()}
          onPress={() => setCaseSensitive((v) => !v)}
        >
          <Icon name="case-sensitive" size={12} />
        </ToggleButton>
      </Tooltip>
      <Tooltip content="Match whole word">
        <ToggleButton
          aria-pressed={wholeWord}
          onMouseDown={(e) => e.preventDefault()}
          onPress={() => setWholeWord((v) => !v)}
        >
          <Icon name="whole-word" size={12} />
        </ToggleButton>
      </Tooltip>
      <Tooltip content="Use regular expression">
        <ToggleButton
          aria-pressed={regexp}
          onMouseDown={(e) => e.preventDefault()}
          onPress={() => setRegexp((v) => !v)}
        >
          <Icon name="regex" size={12} />
        </ToggleButton>
      </Tooltip>
      <Tooltip content="Previous match">
        <ToggleButton
          onMouseDown={(e) => e.preventDefault()}
          onPress={() => view && findPrevious(view)}
        >
          <Icon name="arrow-up" size={12} />
        </ToggleButton>
      </Tooltip>
      <Tooltip content="Next match">
        <ToggleButton
          onMouseDown={(e) => e.preventDefault()}
          onPress={() => view && findNext(view)}
        >
          <Icon name="arrow-down" size={12} />
        </ToggleButton>
      </Tooltip>
      <Tooltip content="Close">
        <ToggleButton onMouseDown={(e) => e.preventDefault()} onPress={onClose}>
          <Icon name="x" size={12} />
        </ToggleButton>
      </Tooltip>
    </Row>
  );
}
